import { useMemo, useRef } from "react";
import { useIsMobile } from "../Layout/useIsMobile";
import AnchorBar from "../AnchorBar";
import Presentation from "../Presentation";
import Slider from "../Slider";

import './PageProjectIndigoNeo.scss';
import Accordion from "../Accordion";

export const PageProjectIndigoNeo = () => {
  const isMobile = useIsMobile();
  const contextRef = useRef(null);
  const resultsRef = useRef(null);
  const searchRef = useRef(null);
  const conclusionRef = useRef(null);
  const sliderItems = useMemo(() => [
    {
      classSuffix: "blissim-gift",
      image: "/images/Blissim/Gift/presentation-mobile.png",
      subtitle: "REFONTE WEB",
      title: "Blissim - Gift",
      url: "/projet/blissim-gift"
    },
    {
      classSuffix: "blissim-rgpd",
      image: "/images/Blissim/RGPD/presentation-mobile.png",
      subtitle: "DESIGN LÉGAL",
      title: "Blissim - RGPD",
      url: "/projet/blissim-rgpd"
    },
    {
      classSuffix: "o-revoir",
      image: "/images/ORevoir/presentation-mobile.png",
      subtitle: "BUSINESS PLAN",
      title: "ô revoir",
      url: "/projet/o-revoir"
    }
  ], []);

  const anchors = useMemo(() => ([
    { id: "context", ref: contextRef, title: "Contexte" },
    { id: "search", ref: searchRef, title: "Analyse & Recherches" },
    { id: "results", ref: resultsRef, title: "Résultats" },
    { id: "conclusion", ref: conclusionRef, title: "Conclusion" },
  ]), []);

  const problemText = "Le défi principal était d’adapter la nouvelle charte print au digital : la palette couleur, pensée pour l’impression, manquait de contraste et posait des problèmes d’accessibilité. En parallèle, le parcours d’achat d’abonnement devait être repensé pour être plus fluide, clair et rassurant.";
  const problemTitle = "Problème à résoudre";

  return (
    <div className="page-project indigo-neo">
      <Presentation
        alt="Indigo Neo icon"
        classSuffix="indigo-neo"
        image={isMobile ? "/images/IndigoNeo/presentation-mobile.png" : "/images/IndigoNeo/presentation-desktop.png"}
        title="Indigo Neo"
        subtitle="PRODUCT DESIGNER / REFONTE"
      />

      <AnchorBar anchors={anchors} />

      <section className="anchor-section">
        <h2 ref={contextRef} id="discover" className="anchor--title">Contexte</h2>
        <hr className="anchor--separator" />

        <div className="discover-information">
          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Objectifs</h3>
            <ul>
              <li>Clarifier la promesse d’Indigo Neo en tant qu’application de stationnement urbain.</li>
              <li>Repenser l’identité visuelle pour la rendre plus accessible, rassurante et cohérente.</li>
              <li>Optimiser les parcours clés pour améliorer la conversion et l’adoption du service.</li>
            </ul>
          </div>

          <div id="tools" className="discover-information-wrapper">
            <h3 className="discover-information--title">Outils</h3>
            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-figma.svg" alt="icon Figma" />
                <span className="icon-tool-title">Figma</span>
              </div>
            </div>
          </div>

          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Durée</h3>
            <p className="discover-information--description">
              ~ 35 semaines
            </p>
          </div>
        </div>

        <div id="problem" className="discover-information-wrapper">
          <Accordion
            accordionName="accordion--discover-information__problem"
            content={problemText}
            label={problemTitle}
          >
            <>
              <h3 className="discover-information--title">{problemTitle}</h3>
              <p className="discover-information--description__full-size" dangerouslySetInnerHTML={{ __html: problemText }} />
            </>
          </Accordion>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={searchRef} id="define" className="anchor--title">Analyse & Recherches</h2>
        <hr className="anchor--separator" />

        <div>
          <h3 className="define--title">Besoins sur le projet :</h3>

          <div className="define--block">
            <div>
              <h4 className="define-block--subtitle">🧑‍💻 Côté utilisateur</h4>
              <ul>
                <li>Améliorer la lisibilité et l’accessibilité de l’interface.</li>
                <li>Créer un parcours d’abonnement plus fluide, rassurant et compréhensible.</li>
                <li>Offrir une expérience cohérente avec l’identité de marque.</li>
              </ul>
            </div>

            <div>
              <h4 className="define-block--subtitle">💼 Côté business</h4>
              <ul>
                <li>Moderniser l’image digitale en alignant la charte avec le rebranding global.</li>
                <li>Optimiser le taux de conversion sur les abonnements.</li>
                <li>Renforcer la perception de fiabilité et de clarté du service.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={resultsRef} id="design" className="anchor--title">Résultats</h2>
        <hr className="anchor--separator" />

        <h3 className="wireframe--main-title">Wireframes</h3>

        <h4 className="wireframe--title">Vision cible</h4>
        <p className="wireframe--subtitle">Élaboration d’une vision cible en wireframe</p>

        <div className="generic-flex-wrapper design--image-container">
          <img alt="Indigo wireframe cible" className="wireframe--image" src="/images/IndigoNeo/indigo-cible.png" />
        </div>

        <div className="generic-flex-wrapper design-wrapper design-wrapper">
          <div className="design-container design-container__half-screen">
            <h4>Refonte du parcours - avant</h4>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="Indigo Neo refonte du parcours avant" className="design--image" src="/images/IndigoNeo/indigo-before-redisign-1.png" />
            </div>
          </div>

          <div className="design-container design-container__half-screen">
            <h4>Refonte du parcours - après</h4>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="Indigo Neo refonte du parcours après" className="design--image" src="/images/IndigoNeo/indigo-after-redisign-1.png" />
            </div>
          </div>
        </div>

        <div className="generic-flex-wrapper design-wrapper">
          <div className="design-container design-container__half-screen">
            <div className="generic-flex-wrapper design--image-container">
              <img alt="Indigo Neo refonte du parcours avant" className="design--image" src="/images/IndigoNeo/indigo-before-redisign-2.png" />
            </div>
          </div>

          <div className="design-container design-container__half-screen">
            <div className="generic-flex-wrapper design--image-container">
              <img alt="Indigo Neo refonte du parcours après" className="design--image" src="/images/IndigoNeo/indigo-after-redisign-2.png" />
            </div>
          </div>
        </div>

        <div className="generic-flex-wrapper design-wrapper">
          <div className="design-container design-container__half-screen">
            <h4>Home page</h4>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="Indigo Neo page d'accueil" className="design--image" src="/images/IndigoNeo/indigo-homepage.png" />
            </div>
          </div>

          <div className="design-container design-container__half-screen">
            <h4>LP nos offres spéciales</h4>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="Indigo Neo landing page" className="design--image" src="/images/IndigoNeo/indigo-landing-page-offers.png" />
            </div>
          </div>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={conclusionRef} id="realize" className="anchor--title">Conclusion</h2>
        <hr className="anchor--separator" />

        <div className="generic-flex-wrapper">
          <div className="define--benchmark-card define--benchmark-card__yellow">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Une nouvelle base solide</h4>

              <p>
                Ce projet a permis de poser les bases d’une identité digitale plus cohérente,
                accessible et contemporaine, en harmonisant la charte print avec les exigences du web.
              </p>
            </div>
          </div>

          <div className="define--benchmark-card define--benchmark-card__green">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Bilan de la refonte</h4>

              <p>
                Au-delà du design, la refonte du parcours d’abonnement a amélioré la lisibilité de l’offre et la
                fluidité de l’expérience, avec pour objectif d’augmenter la conversion et d’installer la marque
                dans un univers plus clair et plus rassurant.
              </p>
              <br />
              <p>👉 Un projet stratégique, à la croisée du branding, de l’UX et de la performance produit.</p>
            </div>
          </div>
        </div>
      </section>

      <Slider sliderItems={sliderItems} />
    </div>
  );
};

export default PageProjectIndigoNeo;